import React, { useEffect, useState } from "react";
import api from "@/lib/api";

import { Users, MapPin, Phone, EnvelopeSimple, Trash, X, CheckCircle, Warning } from "@phosphor-icons/react";
import ServiceAreaEditor from "@/components/leads/ServiceAreaEditor";
const STATUS_META = {
  pending: { label: "Offen", color: "bg-amber-100 text-amber-800" },
  contacted: { label: "Kontaktiert", color: "bg-blue-100 text-blue-800" },
  converted: { label: "Kunde", color: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Verworfen", color: "bg-zinc-200 text-zinc-600" },
};

export default function LeadsPanel({ tenant, onCustomerCreated, onTenantChanged }) {
  const [data, setData] = useState({ leads: [], aggregate: [], total: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterZip, setFilterZip] = useState("");
  const [filterCity, setFilterCity] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = data.leads.filter((l) => {
    if (filterStatus !== "all" && (l.status || "pending") !== filterStatus) return false;
    if (filterZip && !(l.zip || "").startsWith(filterZip)) return false;
    if (filterCity && (l.city || "") !== filterCity) return false;
    return true;
  });

  const setStatus = async (lead_id, status) => {
    await api.patch(`/leads/${lead_id}`, { status });
    await load();
  };
  const del = async (lead_id) => {
    if (!window.confirm("Interessent wirklich löschen?")) return;
    await api.delete(`/leads/${lead_id}`);
    await load();
  };
  const convert = async (lead_id) => {
    if (!window.confirm("Diesen Interessenten als Kunde übernehmen? Name, PLZ, Ort und Straße werden automatisch übernommen.")) return;
    try {
      const r = await api.post(`/leads/${lead_id}/convert`);
      const cust = r.data.customer;
      const already = r.data.already_converted;
      // toast-like feedback
      alert(
        (already ? "Interessent war bereits übernommen. " : "✓ Als Kunde übernommen. ") +
        `${cust.first_name} ${cust.last_name} · ${cust.customer_number}\n` +
        `${cust.location?.street || ""}, ${cust.location?.zip || ""} ${cust.location?.city || ""}`
      );
      onCustomerCreated?.(cust);
      // Signalisiere übergeordneter App: bitte Kunden-Tab öffnen und diesen Kunden anzeigen
      try {
        window.dispatchEvent(new CustomEvent("tonneva:open-customer-from-lead", { detail: { customer_id: cust.customer_id } }));
      } catch (e) { console.warn("[LeadsPanel] request failed", e); }
      await load();
    } catch (e) {
      alert("Fehler bei der Übernahme: " + (e.response?.data?.detail || e.message));
    }
  };

  const copyRevokeLink = async (lead_id) => {
    try {
      const r = await api.get(`/leads/${lead_id}/revoke-link`);
      const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const relative = r.data.url || "";
      // Backend gibt relative URL zurück wenn PUBLIC_BASE_URL nicht gesetzt — mit Frontend-URL präfixen
      const fullUrl = relative.startsWith("http") ? relative : `${window.location.origin}${relative.replace(backendBase, "")}`;
      await navigator.clipboard.writeText(fullUrl);
      alert("Widerruf-Link in Zwischenablage kopiert:\n\n" + fullUrl);
    } catch (e) {
      alert("Konnte Widerruf-Link nicht generieren: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-panel">
      <ServiceAreaEditor tenant={tenant} onTenantChanged={onTenantChanged} />
      {/* Aggregate */}
      <section className="bg-white border border-black/10 p-5" data-testid="leads-aggregate-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
            <Users size={18} weight="bold" /> Interessenten pro Ort
          </h3>
          <div className="text-xs text-zinc-500">Gesamt: <strong>{data.total}</strong> · Offen: <strong>{data.pending}</strong></div>
        </div>
        {data.aggregate.length === 0 ? (
          <div className="text-sm text-zinc-400 text-center py-6">Noch keine Interessenten eingetragen.</div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {data.aggregate.map((a) => (
              <button
                key={`${a.city}-${a.zip}`}
                onClick={() => { setFilterZip(a.zip === "—" ? "" : a.zip); setFilterCity(a.city === "—" ? "" : a.city); setFilterStatus("all"); }}
                className="text-left p-3 border border-black/10 rounded-md hover:border-black/30 hover:shadow-sm transition"
                data-testid={`agg-${a.zip}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-sm">{a.city}</div>
                  <div className="font-heading text-2xl font-black">{a.count}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">PLZ {a.zip}</div>
                <div className="flex gap-2 text-[10px]">
                  {a.pending > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{a.pending} offen</span>}
                  {a.contacted > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{a.contacted} kontakt.</span>}
                  {a.converted > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{a.converted} übern.</span>}
                </div>
                {a.count >= 5 && a.pending >= 5 && (
                  <div className="mt-2 text-[10px] font-bold text-emerald-700 flex items-center gap-1">
                    <Warning size={10} weight="fill" /> Sondertour lohnt sich!
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { k: "all", label: "Alle" },
          { k: "pending", label: "Offen" },
          { k: "contacted", label: "Kontaktiert" },
          { k: "converted", label: "Kunde" },
          { k: "rejected", label: "Verworfen" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilterStatus(f.k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filterStatus === f.k ? "bg-black text-white border-black" : "bg-white border-black/10 text-zinc-600 hover:bg-zinc-50"}`}
            data-testid={`leads-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
        {(filterZip || filterCity) && (
          <button onClick={() => { setFilterZip(""); setFilterCity(""); }} className="text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 inline-flex items-center gap-1" data-testid="clear-zip-filter">
            {filterCity || `PLZ ${filterZip}`} <X size={10} weight="bold" />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-black/10">
        <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold tracking-tight">Interessenten ({filtered.length})</h3>
        </div>
        <div className="divide-y divide-black/5">
          {loading ? (
            <div className="p-8 text-center text-zinc-400 text-sm">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-zinc-400 text-sm">Keine Einträge für aktuellen Filter.</div>
          ) : filtered.map((l) => {
            const st = STATUS_META[l.status || "pending"];
            return (
              <div key={l.lead_id} className="p-4 flex flex-wrap gap-3 items-center" data-testid={`lead-row-${l.lead_id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <strong className="text-sm">{l.first_name} {l.last_name}</strong>
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${st.color}`}>{st.label}</span>
                    {l.submit_count > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">×{l.submit_count}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    <a href={`mailto:${l.email}`} className="hover:underline flex items-center gap-1"><EnvelopeSimple size={10} weight="bold" /> {l.email}</a>
                    {l.phone && <a href={`tel:${l.phone.replace(/\s/g,"")}`} className="hover:underline flex items-center gap-1"><Phone size={10} weight="bold" /> {l.phone}</a>}
                    <span className="flex items-center gap-1"><MapPin size={10} weight="bold" /> {l.street}, {l.zip} {l.city}</span>
                  </div>
                  {l.message && <div className="mt-1 text-xs text-zinc-600 italic">„{l.message}&ldquo;</div>}
                  {l.privacy_ack_meta && (
                    <div className="mt-1 text-[10px] text-emerald-700 flex flex-wrap gap-x-2 gap-y-0" data-testid={`consent-${l.lead_id}`}>
                      <span title="DSGVO-Consent-Nachweis">🔒 Datenschutz bestätigt</span>
                      <span className="text-zinc-500">
                        {l.privacy_ack_meta.timestamp && new Date(l.privacy_ack_meta.timestamp).toLocaleString("de-DE")}
                        {l.privacy_ack_meta.ip && ` · IP ${l.privacy_ack_meta.ip}`}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {(l.status || "pending") === "pending" && (
                    <button onClick={() => setStatus(l.lead_id, "contacted")} className="text-[10px] px-2 py-1 border border-black/10 hover:bg-blue-50" data-testid={`mark-contacted-${l.lead_id}`}>→ Kontaktiert</button>
                  )}
                  {(l.status || "pending") !== "converted" && (l.status || "pending") !== "revoked" && (
                    <button onClick={() => convert(l.lead_id)} className="text-[10px] px-2 py-1 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1" data-testid={`convert-${l.lead_id}`}>
                      <CheckCircle size={10} weight="bold" /> Als Kunde
                    </button>
                  )}
                  {l.privacy_ack_meta && (
                    <>
                      <a
                        href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/leads/${l.lead_id}/consent.pdf`}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] px-2 py-1 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                        data-testid={`consent-pdf-${l.lead_id}`}
                        title="DSGVO-Consent-Nachweis als PDF"
                      >
                        📄 Consent-PDF
                      </a>
                      {(l.status || "pending") !== "revoked" && (
                        <button
                          onClick={() => copyRevokeLink(l.lead_id)}
                          className="text-[10px] px-2 py-1 border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1"
                          data-testid={`revoke-link-${l.lead_id}`}
                          title="Widerruf-Link kopieren (für Weitergabe an Kunden)"
                        >
                          🔗 Widerruf-Link
                        </button>
                      )}
                    </>
                  )}
                  {(l.status || "pending") !== "rejected" && (l.status || "pending") !== "converted" && (l.status || "pending") !== "revoked" && (
                    <button onClick={() => setStatus(l.lead_id, "rejected")} className="text-[10px] px-2 py-1 border border-black/10 hover:bg-zinc-50 text-zinc-500" data-testid={`reject-${l.lead_id}`}>Verwerfen</button>
                  )}
                  <button onClick={() => del(l.lead_id)} className="text-[10px] px-2 py-1 border border-black/10 hover:bg-red-50 text-red-600 inline-flex items-center gap-1" data-testid={`delete-lead-${l.lead_id}`}>
                    <Trash size={10} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
